/**
 * Canonical field-of-study catalogue — the SINGLE source of truth shared by
 * the onboarding "About you" step and the social Complete-Profile / Edit dialog
 * (and, later, mobile + search). The stable kebab-case `id` is what gets stored
 * (in `sync.userInfo.fieldOfStudy` at onboarding and `social_profiles.field_of_study`
 * in Postgres); the human label is resolved from i18n at render time via the
 * `fieldOfStudy.<id>` namespace (PLAIN TEXT on both platforms — the emoji
 * prefix was dropped on web so the label matches the social dialogs and
 * search UI, and mobile mirrors web's values verbatim).
 *
 * This is the onboarding taxonomy (its original 22 ids, order preserved so the
 * existing dropdown and every stored `userInfo.fieldOfStudy` value keep
 * resolving) extended with a few naturally-missing student fields. The list
 * order is the order the pickers show, so a later id (`biotechnology`,
 * `physiotherapy`, `literature`, …) sits with its siblings rather than at the
 * tail. Ids are the contract — add/rename with care, never repurpose or drop
 * an existing id (existing users' stored values reference them).
 */

import { normalizeName } from './normalize';

export const FIELD_OF_STUDY_IDS = [
  // ── Original onboarding taxonomy (ids preserved), plus later ids marked
  //    "added" placed beside their siblings ──
  'high-school',
  'medicine',
  'biology',
  'biotechnology', // added 2026-09 — grouped with biology
  'pharmacy',
  'dentistry',
  'nursing',
  'physiotherapy', // added 2026-09 — grouped with the health sciences
  'veterinary',
  'chemistry',
  'law',
  'languages',
  'literature', // added 2026-09
  'business',
  'history',
  'philosophy', // added 2026-09
  'physics',
  'mathematics',
  'engineering',
  'computer-science',
  'politics',
  'geography',
  'marketing',
  'communication', // added 2026-09
  'design', // Figma label: "Design / Architecture"
  'sports',
  // ── Naturally-missing student fields added for the social layer ──
  'psychology',
  'economics',
  'education',
  // ── Catch-all, always last ──
  'other',
] as const;

export type FieldOfStudyId = (typeof FIELD_OF_STUDY_IDS)[number];

/**
 * The catch-all field. Every field-of-study picker keeps it on screen when a
 * search matches nothing (`searchFallback` on its option), so a student whose
 * course is not in the list still has an answer to pick.
 */
export const FIELD_OF_STUDY_FALLBACK_ID: FieldOfStudyId = 'other';

const FIELD_OF_STUDY_ID_SET: ReadonlySet<string> = new Set(FIELD_OF_STUDY_IDS);

/** Type-guard used at the Postgres / API boundary before persisting a field id. */
export function isFieldOfStudyId(value: unknown): value is FieldOfStudyId {
  return typeof value === 'string' && FIELD_OF_STUDY_ID_SET.has(value);
}

/**
 * Canonical ENGLISH labels — kept as a public export for legacy callers and
 * because English is the taxonomy's stable reference language. The people-search
 * matches against these labels PLUS the localized labels below (see
 * `FIELD_OF_STUDY_LOCALE_LABELS`), so a Polish user typing "Informatyka" hits
 * `computer-science` even though the profile is stored as the kebab id. Compound
 * labels keep every searchable word so, e.g., "pharmacology" resolves.
 */
export const FIELD_OF_STUDY_EN_LABELS: Record<FieldOfStudyId, string> = {
  'high-school': 'High School',
  medicine: 'Medicine',
  biology: 'Biology',
  biotechnology: 'Biotechnology',
  pharmacy: 'Pharmacy Pharmacology',
  dentistry: 'Dentistry',
  nursing: 'Nursing',
  physiotherapy: 'Physiotherapy Physical Therapy',
  veterinary: 'Veterinary',
  chemistry: 'Chemistry',
  law: 'Law',
  languages: 'Languages',
  literature: 'Literature Humanities',
  business: 'Business studies',
  history: 'History',
  philosophy: 'Philosophy',
  physics: 'Physics Astronomy',
  mathematics: 'Mathematics',
  engineering: 'Engineering',
  'computer-science': 'Computer Science',
  politics: 'Politics',
  geography: 'Geography',
  marketing: 'Marketing',
  communication: 'Communication Media Studies',
  design: 'Design Architecture',
  sports: 'Sports',
  psychology: 'Psychology',
  economics: 'Economics',
  education: 'Education',
  other: 'Other',
};

/**
 * Localized labels for every language the app ships in. Mirrors the
 * `fieldOfStudy.<id>` i18n keys in `flashka-web/src/locales/<lng>/<lng>-onboarding.json`
 * (and the mobile equivalents). Kept here — not read from the i18n bundles at
 * runtime — because `matchFieldOfStudyIds` runs on the party server where the
 * client i18n resources aren't available; the taxonomy is closed and small, so
 * duplicating it is cheaper than plumbing an i18n dependency into shared/.
 *
 * Keep in sync with the onboarding locale files when adding a language or
 * renaming a label. The matcher splits each label on '/' segments (see below),
 * so labels like "Design / Architektura" contribute both halves as candidates.
 *
 * EXPORTED for `fields-of-study.spec.ts`, which pins that hand-sync against the
 * actual locale bundles on both platforms — a label that drifts here is a label
 * a user reads, types into people-search, and gets zero results for. Nothing
 * else should read this table: consumers want `matchFieldOfStudyIds` (search)
 * or their own i18n `fieldOfStudy.<id>` key (display).
 */
export const FIELD_OF_STUDY_LOCALE_LABELS: Record<FieldOfStudyId, readonly string[]> = {
  'high-school': [
    'High School',
    'Szkoła średnia',
    'Gymnasium / Sekundarstufe',
    'Enseñanza Secundaria',
    'Lycée',
    'Scuola Superiore',
    'תיכון',
    'Gymnasium',
    'Ensenyament Secundari',
  ],
  medicine: [
    'Medicine',
    'Medycyna',
    'Medizin',
    'Medicina',
    'Médecine',
    'Medicina',
    'רפואה',
    'Medicin',
    'Medicina',
  ],
  biology: [
    'Biology',
    'Biologia',
    'Biologie',
    'Biología',
    'Biologie',
    'Biologia',
    'ביולוגיה',
    'Biologi',
    'Biologia',
  ],
  biotechnology: [
    'Biotechnology',
    'Biotechnologia',
    'Biotechnologie',
    'Biotecnología',
    'Biotechnologie',
    'Biotecnologie',
    'ביוטכנולוגיה',
    'Bioteknik',
    'Biotecnologia',
  ],
  pharmacy: [
    'Pharmacy / Pharmacology',
    'Farmacja / Farmakologia',
    'Pharmazie / Pharmakologie',
    'Farmacia / Farmacología',
    'Pharmacie / Pharmacologie',
    'Farmacia / Farmacologia',
    'רוקחות / פרמקולוגיה',
    'Farmaci / Farmakologi',
    'Farmàcia / Farmacologia',
  ],
  dentistry: [
    'Dentistry',
    'Stomatologia',
    'Zahnmedizin',
    'Odontología',
    'Dentisterie',
    'Odontoiatria',
    'רפואת שיניים',
    'Odontologi',
    'Odontologia',
  ],
  nursing: [
    'Nursing',
    'Pielęgniarstwo',
    'Krankenpflege',
    'Enfermería',
    'Soins infirmiers',
    'Infermieristica',
    'סיעוד',
    'Omvårdnad',
    'Infermeria',
  ],
  physiotherapy: [
    'Physiotherapy',
    'Fizjoterapia',
    'Physiotherapie',
    'Fisioterapia',
    'Kinésithérapie',
    'Fisioterapia',
    'פיזיותרפיה',
    'Fysioterapi',
    'Fisioteràpia',
    // Search-only aliases (not a shipped label): the Belgian/Swiss/Québécois
    // name for the degree, and the older Swedish name students still type.
    'Physiothérapie',
    'Sjukgymnastik',
  ],
  veterinary: [
    'Veterinary',
    'Weterynaria',
    'Tiermedizin',
    'Veterinaria',
    'Médecine vétérinaire',
    'Veterinaria',
    'וטרינריה',
    'Veterinärmedicin',
    'Veterinària',
  ],
  chemistry: [
    'Chemistry',
    'Chemia',
    'Chemie',
    'Química',
    'Chimie',
    'Chimica',
    'כימיה',
    'Kemi',
    'Química',
  ],
  law: [
    'Law',
    'Prawo',
    'Jura / Rechtswissenschaft',
    'Derecho',
    'Droit',
    'Giurisprudenza',
    'משפטים',
    'Juridik',
    'Dret',
  ],
  languages: [
    'Languages',
    'Języki',
    'Sprachen',
    'Idiomas',
    'Langues',
    'Lingue',
    'שפות',
    'Språk',
    'Llengües',
  ],
  literature: [
    'Literature',
    'Literatura / Filologia',
    'Literaturwissenschaft',
    'Letras / Filología',
    'Lettres',
    'Lettere',
    'ספרות',
    'Litteraturvetenskap',
    'Lletres / Filologia',
  ],
  business: [
    'Business studies',
    'Biznes / Studia biznesowe',
    'Betriebswirtschaftslehre (BWL)',
    'Administración de Empresas',
    'Études commerciales',
    'Economia',
    'מנהל עסקים',
    'Företagsekonomi',
    "Administració d'Empreses",
  ],
  history: [
    'History',
    'Historia',
    'Geschichte',
    'Historia',
    'Histoire',
    'Storia',
    'היסטוריה',
    'Historia',
    'Història',
  ],
  philosophy: [
    'Philosophy',
    'Filozofia',
    'Philosophie',
    'Filosofía',
    'Philosophie',
    'Filosofia',
    'פילוסופיה',
    'Filosofi',
    'Filosofia',
  ],
  physics: [
    'Physics / Astronomy',
    'Fizyka / Astronomia',
    'Physik / Astronomie',
    'Física / Astronomía',
    'Physique / Astronomie',
    'Fisica / Astronomia',
    'פיזיקה / אסטרונומיה',
    'Fysik / Astronomi',
    'Física / Astronomia',
  ],
  mathematics: [
    'Mathematics',
    'Matematyka',
    'Mathematik',
    'Matemáticas',
    'Mathématiques',
    'Matematica',
    'מתמטיקה',
    'Matematik',
    'Matemàtiques',
  ],
  engineering: [
    'Engineering',
    'Inżynieria',
    'Ingenieurwesen',
    'Ingeniería',
    'Ingénierie',
    'Ingegneria',
    'הנדסה',
    'Ingenjörsvetenskap',
    'Enginyeria',
  ],
  'computer-science': [
    'Computer Science',
    'Informatyka',
    'Informatik',
    'Informática',
    'Informatique',
    'Informatica',
    'מדעי המחשב',
    'Datorvetenskap',
    'Informàtica',
  ],
  politics: [
    'Politics',
    'Polityka',
    'Politik',
    'Ciencias Políticas',
    'Politique',
    'Scienze Politiche',
    'מדעי המדינה',
    'Politik',
    'Ciències Polítiques',
  ],
  geography: [
    'Geography',
    'Geografia',
    'Geografie',
    'Geografía',
    'Géographie',
    'Geografia',
    'גאוגרפיה',
    'Geografi',
    'Geografia',
  ],
  marketing: [
    'Marketing',
    'Marketing',
    'Marketing',
    'Marketing',
    'Marketing',
    'Marketing',
    'שיווק',
    'Marknadsföring',
    'Màrqueting',
  ],
  communication: [
    'Communication / Media Studies',
    'Dziennikarstwo i komunikacja społeczna',
    'Medien- und Kommunikationswissenschaft',
    'Ciencias de la Comunicación',
    'Sciences de la communication',
    'Scienze della comunicazione',
    'תקשורת',
    'Medie- och kommunikationsvetenskap',
    'Ciències de la Comunicació',
  ],
  design: [
    'Design / Architecture',
    'Design / Architektura',
    'Design / Architektur',
    'Diseño / Arquitectura',
    'Design / Architecture',
    'Design / Architettura',
    'עיצוב / אדריכלות',
    'Design / Arkitektur',
    'Disseny / Arquitectura',
  ],
  sports: [
    'Sports',
    'Sport',
    'Sport',
    'Deportes',
    'Sport',
    'Sport',
    'ספורט',
    'Sport',
    'Esports',
  ],
  psychology: [
    'Psychology',
    'Psychologia',
    'Psychologie',
    'Psicología',
    'Psychologie',
    'Psicologia',
    'פסיכולוגיה',
    'Psykologi',
    'Psicologia',
  ],
  economics: [
    'Economics',
    'Ekonomia',
    'Wirtschaftswissenschaften',
    'Economía',
    'Économie',
    'Economia',
    'כלכלה',
    'Ekonomi',
    'Economia',
  ],
  education: [
    'Education',
    'Edukacja',
    'Pädagogik',
    'Educación',
    'Éducation',
    'Pedagogia',
    'חינוך',
    'Pedagogik',
    'Educació',
  ],
  // `other` intentionally has no localized candidates — matching "Inne" / "Otros" /
  // "Sonstiges" would let anyone typing those words enumerate every profile whose
  // field wasn't set to a real subject. Keep it id/English-only.
  other: [],
};

/**
 * Pre-computed normalized candidates per id (labels + `/`-split segments + the
 * id itself with dashes as spaces). Built once at module load; the matcher only
 * walks strings, not `normalizeName` on every keystroke.
 */
const FIELD_OF_STUDY_CANDIDATES: Record<FieldOfStudyId, readonly string[]> = (() => {
  const out = {} as Record<FieldOfStudyId, readonly string[]>;
  for (const id of FIELD_OF_STUDY_IDS) {
    const raw: string[] = [id.replace(/-/g, ' '), FIELD_OF_STUDY_EN_LABELS[id]];
    for (const label of FIELD_OF_STUDY_LOCALE_LABELS[id]) {
      raw.push(label);
      for (const segment of label.split('/')) raw.push(segment);
    }
    const seen = new Set<string>();
    for (const s of raw) {
      const n = normalizeName(s);
      if (n.length >= 2) seen.add(n);
    }
    out[id] = Array.from(seen);
  }
  return out;
})();

/**
 * Resolve a raw search query to the field-of-study ids it matches, using the
 * SAME prefix / word-boundary semantics as the Postgres LIKE search: a candidate
 * matches when the normalized query is a prefix of a candidate word. Candidates
 * per id are its id (dashes → spaces), its English label, AND every localized
 * label the app ships in (split on `/` segments), so "computer", "science",
 * "informatyka", "informatik" all hit `computer-science`. Returns [] for a
 * query under two normalized characters (mirrors the search min-length rule).
 */
/**
 * Drop a label's leading emoji, if it has one.
 *
 * The `fieldOfStudy.*` labels are plain text on both platforms today (web
 * dropped the emoji prefix; mobile mirrors web's values). This trim survives
 * as a BELT: a re-translated locale file that sneaks an emoji back in must not
 * change what a profile chip or search row renders. Mobile's field-label hook
 * is the caller.
 *
 * `p{Extended_Pictographic}` covers the full emoji range; ZWJ sequences and
 * variation selectors are stripped alongside it.
 */
export function stripLeadingEmoji(label: string): string {
  // Written as an ALTERNATION, not a character class: ESLint's
  // `no-misleading-character-class` rightly objects to putting a combining
  // character (ZWJ / variation selector) inside `[...]`, where it would read as
  // a standalone member rather than part of a sequence.
  return label.replace(/^(?:\p{Extended_Pictographic}|\u200d|\ufe0f|\s)+/u, '');
}

export function matchFieldOfStudyIds(rawQuery: string): FieldOfStudyId[] {
  const q = normalizeName(rawQuery);
  if (q.length < 2) return [];
  return FIELD_OF_STUDY_IDS.filter((id) =>
    FIELD_OF_STUDY_CANDIDATES[id].some(
      (candidate) => candidate.startsWith(q) || candidate.includes(` ${q}`),
    ),
  );
}

/** Each field's whole label key (`fieldOfStudy.<id>`), in both apps' locales. */
export const FIELD_OF_STUDY_LABEL_KEY = {
  'high-school': 'fieldOfStudy.high-school',
  medicine: 'fieldOfStudy.medicine',
  biology: 'fieldOfStudy.biology',
  biotechnology: 'fieldOfStudy.biotechnology',
  pharmacy: 'fieldOfStudy.pharmacy',
  dentistry: 'fieldOfStudy.dentistry',
  nursing: 'fieldOfStudy.nursing',
  physiotherapy: 'fieldOfStudy.physiotherapy',
  veterinary: 'fieldOfStudy.veterinary',
  chemistry: 'fieldOfStudy.chemistry',
  law: 'fieldOfStudy.law',
  languages: 'fieldOfStudy.languages',
  literature: 'fieldOfStudy.literature',
  business: 'fieldOfStudy.business',
  history: 'fieldOfStudy.history',
  philosophy: 'fieldOfStudy.philosophy',
  physics: 'fieldOfStudy.physics',
  mathematics: 'fieldOfStudy.mathematics',
  engineering: 'fieldOfStudy.engineering',
  'computer-science': 'fieldOfStudy.computer-science',
  politics: 'fieldOfStudy.politics',
  geography: 'fieldOfStudy.geography',
  marketing: 'fieldOfStudy.marketing',
  communication: 'fieldOfStudy.communication',
  design: 'fieldOfStudy.design',
  sports: 'fieldOfStudy.sports',
  psychology: 'fieldOfStudy.psychology',
  economics: 'fieldOfStudy.economics',
  education: 'fieldOfStudy.education',
  other: 'fieldOfStudy.other',
} as const satisfies Record<FieldOfStudyId, string>;
